import {
  BaseCard,
  Primary,
  SvgIcon,
  Text,
  TextLink,
} from '@cinch-labs/design-system'
import styles from './choose-store-card.module.css'
import { MouseEventHandler, useEffect, useState } from 'react'
import {
  useAnalytics,
  EventNames,
  TrackingEventTypes,
} from '@cinch-nx/shared-analytics'
import cx from 'classnames'
import { Coordinates, OpeningHours } from '../../hooks/types'
import useIsMobile from '../../hooks/use-is-mobile'
import { formatOpeningHours } from '../../utils/format-opening-hours'

type StoreCardProps = {
  index: number
  distance: number
  name: string
  address: string
  coordinates: Coordinates
  onSelect: () => void
  isSelected: boolean
  onMapClick: () => void
  showMapLink: boolean
  customerPostcode?: string
  ariaLabel?: string
  openingHours?: OpeningHours
  displayName: string
  under60Months: boolean
  distanceFromOEM?: number
}

export const ChooseStoreCard: React.FC<StoreCardProps> = ({
  index,
  distance,
  name,
  address,
  coordinates,
  onSelect,
  isSelected,
  onMapClick,
  showMapLink,
  customerPostcode,
  ariaLabel,
  openingHours,
  displayName,
  under60Months,
  distanceFromOEM,
}) => {
  const { trackEvent } = useAnalytics()
  const { isMobile } = useIsMobile()
  const [showMoreInfo, setShowMoreInfo] = useState(false)

  useEffect(() => {
    if (index === 0 && distance > 0) {
      const oemOption = under60Months && distanceFromOEM && distanceFromOEM < 21
      trackEvent({
        eventName: `Load stores`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.load,
            category: 'cinch_servicing',
            action: oemOption
              ? 'store_list_displayed_OEM_options'
              : 'store_list_displayed',
            label: oemOption ? 'make_a_booking_OEM_options' : 'make_a_booking',
            type: name,
            value: distance,
          },
        },
      })
    }
  }, [index])

  const onCardClick = (event: { target: { id: string } }) => {
    if (
      event.target.id !== 'choose-service-centre-button' &&
      event.target.id !== 'choose-service-centre-button-text' &&
      event.target.id !== 'service-centre-map-link' &&
      event.target.id !== 'see-more-info-link'
    ) {
      onMapClick()
    }
  }

  const handleToggleInfo = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newState = !showMoreInfo
    setShowMoreInfo(newState)

    trackEvent({
      eventName: `Toggle Store Info`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: newState ? 'see_more_info' : 'see_less_info',
          label: name,
          type: 'list_view',
        },
      },
    })
  }

  return (
    <BaseCard
      className={cx(
        styles.card,
        index === 0 ? '' : styles.marginTop,
        isSelected ? styles.selected : '',
      )}
    >
      <div
        onClick={onCardClick as unknown as MouseEventHandler<HTMLDivElement>}
      >
        <Text
          element="h3"
          fontSizeFixed="xl"
          fontWeight="bold"
          className={styles.name}
        >
          {displayName}
        </Text>

        <Text fontSizeFixed="s" className={styles.address}>
          {address}
        </Text>

        {distance !== 0 && (
          <Text fontSizeFixed="s" className={styles.distance}>
            {distance} miles away
          </Text>
        )}

        {isMobile && openingHours && (
          <>
            <div className={styles.seeMoreLink}>
              <TextLink
                onClick={handleToggleInfo}
                data-testid={'see-more-info-link'}
                id={'see-more-info-link'}
              >
                <Text fontSizeFixed="s">
                  <SvgIcon name="info" />{' '}
                  {showMoreInfo ? 'See less info' : 'See more info'}
                </Text>
              </TextLink>
            </div>

            {showMoreInfo && (
              <div className={styles.expandedInfo}>
                <Text
                  fontSizeFixed="m"
                  fontWeight="bold"
                  className={styles.hoursTitle}
                >
                  Opening hours
                </Text>
                {formatOpeningHours(openingHours).map(({ day, hours }) => (
                  <div key={day} className={styles.hoursRow}>
                    <Text fontSizeFixed="s">{day}</Text>
                    <Text fontSizeFixed="s">{hours}</Text>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {showMapLink && customerPostcode && !isMobile && (
          <div className={styles.mapLink}>
            <TextLink
              onClick={onMapClick}
              element={'a'}
              href={`https://www.google.com/maps/dir/?api=1&origin=${customerPostcode}&destination=${coordinates.latitude},${coordinates.longitude}`}
              target={'_blank'}
              data-testid={'service-centre-map-link'}
              id={'service-centre-map-link'}
            >
              <Text fontSizeFixed="s">
                <SvgIcon name="new-tab" /> Map and directions
              </Text>
            </TextLink>
          </div>
        )}

        <Primary
          onClick={onSelect}
          size="large"
          classNames={{ button: styles.button }}
          aria-label={ariaLabel || `Choose ${name}`}
          data-testid={'choose-service-centre-button'}
          id={'choose-service-centre-button'}
        >
          <div
            data-testid={'choose-service-centre-button-text'}
            id={'choose-service-centre-button-text'}
          >
            {`Choose service centre`}
          </div>
        </Primary>
      </div>
    </BaseCard>
  )
}
